// SPDX-License-Identifier: AGPL-3.0-or-later

import {CompanionSystemAudio} from '@app/features/companion/audio/CompanionSystemAudio';
import {CompanionVoiceOutput} from '@app/features/companion/audio/CompanionVoiceOutput';
import type {CompanionAudioController} from '@app/features/companion/state/CompanionSessionStore';
import {
	acquireVoiceInputContext,
	acquireVoiceInputSource,
	type VoiceInputContextLease,
	type VoiceInputSourceLease,
} from '@app/features/voice/engine/VoiceInputAudioContext';
import {VoiceInputTrackProcessor} from '@app/features/voice/utils/VoiceInputProcessor';
import {LocalAudioTrack, Room, RoomEvent, Track} from 'livekit-client';

export interface CompanionMicrophoneState {
	muted: boolean;
	roomTrack: MediaStreamTrack | null;
	capture: MediaTrackConstraints;
	captureKey: string;
}

/** Owns only its private connection and cloned/locally acquired tracks. */
export class CompanionMicrophone implements CompanionAudioController {
	private readonly output: CompanionVoiceOutput;
	private readonly systemAudio: CompanionSystemAudio;
	private room: Room | null = null;
	private track: LocalAudioTrack | null = null;
	private borrowedTrack: MediaStreamTrack | null = null;
	private processor: VoiceInputTrackProcessor | null = null;
	private inputContextLease: VoiceInputContextLease | null = null;
	private meterInput: VoiceInputSourceLease | null = null;
	private meterAnalyser: AnalyserNode | null = null;
	private meter: ReturnType<typeof setInterval> | null = null;
	private epoch = 0;
	private paused = false;
	private connected = false;
	private appliedCaptureKey = '';
	private serial: Promise<void> = Promise.resolve();
	private state: CompanionMicrophoneState = {muted: true, roomTrack: null, capture: {}, captureKey: ''};

	constructor(
		private readonly onInput: (level: number, label: string) => void,
		private readonly onFailure: (error: unknown, endSession?: boolean) => void,
		playbackBlocked: (blocked: boolean) => void = () => {},
	) {
		this.systemAudio = new CompanionSystemAudio((error) => onFailure(error, true));
		this.output = new CompanionVoiceOutput(playbackBlocked);
	}

	configureOutput(volume: number, sinkId: string): void {
		this.output.configure(volume, sinkId);
	}
	resumePlayback(): Promise<void> {
		return this.output.resume();
	}

	prepareSystemAudio(): Promise<void> {
		return this.systemAudio.prepare();
	}

	async sendText(text: string): Promise<boolean> {
		// 与 agent 约定: 普通数据通道 topic companion.text, 规避两端文本流协议版本差异。
		const participant = this.room?.localParticipant;
		if (!participant || !this.connected) return false;
		try {
			await participant.publishData(new TextEncoder().encode(text), {topic: 'companion.text', reliable: true});
			return true;
		} catch (error) {
			console.error('[companion] sendText failed', error);
			return false;
		}
	}

	updateState(state: CompanionMicrophoneState): void {
		this.state = state;
		// Stop transmission synchronously; asynchronous device operations cannot delay mute.
		if (state.muted && this.track) this.track.mediaStreamTrack.enabled = false;
		void this.enqueue().catch(this.onFailure);
	}

	async connect(
		connection: {endpoint: string; token: string},
		systemConnection?: {endpoint: string; token: string},
		playback?: {participantIdentity: string},
	): Promise<void> {
		this.disconnectMicrophone();
		this.paused = false;
		const epoch = this.epoch;
		const room = new Room();
		if (playback) this.output.bind(room, playback.participantIdentity);
		this.room = room;
		room.on(RoomEvent.Disconnected, () => {
			if (this.room !== room) return;
			this.disconnect();
			this.onFailure(new Error('Companion microphone disconnected. End this session and start again.'));
		});
		await room.connect(connection.endpoint, connection.token, {autoSubscribe: !!playback});
		if (epoch !== this.epoch || room !== this.room) {
			await room.disconnect();
			throw new DOMException('Companion connection cancelled', 'AbortError');
		}
		this.connected = true;
		await this.enqueue();
		if (systemConnection) await this.systemAudio.connect(systemConnection);
	}

	setPaused(paused: boolean): void {
		this.paused = paused;
		this.systemAudio.setPaused(paused);
		this.output.setPaused(paused);
		if (paused && this.track) this.track.mediaStreamTrack.enabled = false;
		void this.enqueue().catch(this.onFailure);
	}

	disconnect(): void {
		this.systemAudio.disconnect();
		this.disconnectMicrophone();
	}

	private disconnectMicrophone(): void {
		this.epoch += 1;
		this.connected = false;
		this.output.disconnect();
		const room = this.room;
		this.room = null;
		this.releaseTrack();
		if (room) void room.disconnect().catch(() => {});
	}

	private enqueue(): Promise<void> {
		const epoch = this.epoch;
		this.serial = this.serial.catch(() => {}).then(() => this.reconcile(epoch));
		return this.serial;
	}

	private releaseTrack(): void {
		if (this.meter) clearInterval(this.meter);
		this.meter = null;
		if (this.track) {
			this.track.mediaStreamTrack.enabled = false;
			this.track.stop();
		}
		this.track = null;
		this.borrowedTrack = null;
		if (this.processor) void this.processor.destroy();
		this.processor = null;
		this.meterInput?.source.disconnect();
		this.meterAnalyser?.disconnect();
		this.meterInput?.lease.release();
		this.meterInput = null;
		this.meterAnalyser = null;
		this.inputContextLease?.release();
		this.inputContextLease = null;
		this.appliedCaptureKey = '';
		this.onInput(0, '');
	}

	private async removeTrack(room: Room): Promise<void> {
		const track = this.track;
		this.releaseTrack();
		if (track) await room.localParticipant.unpublishTrack(track);
	}

	private async reconcile(epoch: number): Promise<void> {
		const room = this.room;
		if (epoch !== this.epoch || !room || !this.connected) return;
		if (this.state.muted || this.paused) {
			await this.removeTrack(room);
			return;
		}
		const borrowed = this.state.roomTrack?.readyState === 'live' ? this.state.roomTrack : null;
		if (
			this.track &&
			this.borrowedTrack === borrowed &&
			(borrowed || this.appliedCaptureKey === this.state.captureKey)
		) {
			await this.processor?.configure();
			return;
		}
		await this.removeTrack(room);
		const key = this.state.captureKey;
		const stream = borrowed
			? new MediaStream([borrowed.clone()])
			: await navigator.mediaDevices.getUserMedia({audio: this.state.capture});
		const raw = stream.getAudioTracks()[0];
		if (!raw) {
			stream.getTracks().forEach((track) => track.stop());
			throw new Error('No microphone audio track');
		}
		if (
			epoch !== this.epoch ||
			this.paused ||
			this.state.muted ||
			key !== this.state.captureKey ||
			room !== this.room
		) {
			stream.getTracks().forEach((track) => track.stop());
			return;
		}
		if (!borrowed) {
			this.inputContextLease = acquireVoiceInputContext();
			if (!this.inputContextLease) {
				raw.stop();
				throw new Error('Microphone audio context is unavailable');
			}
		}
		const track = new LocalAudioTrack(raw, undefined, true, this.inputContextLease?.context);
		this.track = track;
		this.borrowedTrack = borrowed;
		this.appliedCaptureKey = key;
		try {
			if (!borrowed) {
				this.processor = new VoiceInputTrackProcessor(1, {
					onSourceRateChanged: () => this.onFailure(new Error('Microphone sample rate changed. Restart companion.')),
					onGraphStalled: () => this.onFailure(new Error('Microphone audio processing stalled. Restart companion.')),
					onGraphFailed: () => this.onFailure(new Error('Microphone audio processing failed. Restart companion.')),
				});
				await track.setProcessor(this.processor);
			}
			if (epoch !== this.epoch || this.paused || this.state.muted) {
				this.releaseTrack();
				return;
			}
			await room.localParticipant.publishTrack(track, {source: Track.Source.Microphone});
			if (epoch !== this.epoch || this.paused || this.state.muted) {
				await this.removeTrack(room);
				return;
			}
			const meterInput = acquireVoiceInputSource(track.mediaStreamTrack);
			if (meterInput) {
				this.meterInput = meterInput;
				const context = meterInput.lease.context;
				void context.resume().catch(() => {});
				const analyser = context.createAnalyser();
				this.meterAnalyser = analyser;
				analyser.fftSize = 512;
				meterInput.source.connect(analyser);
				const samples = new Float32Array(analyser.fftSize);
				this.meter = setInterval(() => {
					analyser.getFloatTimeDomainData(samples);
					const rms = Math.sqrt(samples.reduce((sum, value) => sum + value * value, 0) / samples.length);
					this.onInput(Math.min(1, rms * 4), raw.label);
				}, 100);
			}
		} catch (error) {
			if (this.track === track) this.releaseTrack();
			else track.stop();
			throw error;
		}
	}
}
